import { DEFAULT_WALL_THICKNESS } from "@gettactic/map-builder/src/components/map/defines";
import {
  createWall,
  createWallPath
} from "@gettactic/map-builder/src/utils/shapeFactory";
import {
  snapToWallEndpoint,
  type WallSnapResult
} from "@gettactic/map-builder/src/utils/snap";
import { splitWallAtPoint } from "@gettactic/map-builder/src/utils/wallAttach";
import { addShapes, type DocumentState } from "../document/operations";
import { documentFromShapes, orderedShapes } from "../document/serialize";
import { isPrimaryButton } from "../gesture/pointer";
import type { Point } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

const constrainAngle = (origin: Point, point: Point): Point => {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const distance = Math.hypot(dx, dy);
  return {
    x: origin.x + distance * Math.cos(angle),
    y: origin.y + distance * Math.sin(angle)
  };
};

const snapWallPoint = (
  context: ToolContext,
  point: Point,
  bypass: boolean
): WallSnapResult => {
  if (bypass) return { ...point, snapped: false };
  const state = context.store.getState();
  return snapToWallEndpoint(
    point,
    orderedShapes(state),
    state.viewport.scale,
    undefined,
    { includeSegments: true }
  );
};

/** Splitting an existing wall works on the saved array form; convert
 *  there and back so the pure util is reused as is. */
const splitAt = (
  document: DocumentState,
  hit: NonNullable<WallSnapResult["segment"]> & Point
): DocumentState => {
  const list = orderedShapes(document);
  const next = splitWallAtPoint(list, hit);
  if (next === list) return document;
  return documentFromShapes(next);
};

/**
 * Click to start, click to finish. Ends snap to wall endpoints and
 * segment interiors (Alt bypasses), Shift constrains the angle and chains
 * the next wall from the end point, as in the Konva editor.
 */
export const createWallTool = (): Tool => {
  let start: Point | null = null;
  let context: ToolContext | null = null;

  const frame = createFrameScheduler<{
    client: Point;
    shiftKey: boolean;
    altKey: boolean;
  }>(({ client, shiftKey, altKey }) => {
    if (!context || !start) return;
    const raw = context.snap.snapPoint(context.toWorld(client));
    const candidate = shiftKey ? constrainAngle(start, raw) : raw;
    const snapped = snapWallPoint(context, candidate, altKey);
    context.overlay.set({
      draft: {
        kind: "wall",
        from: start,
        to: { x: snapped.x, y: snapped.y },
        thickness: DEFAULT_WALL_THICKNESS
      },
      guides:
        snapped.alignX !== undefined || snapped.alignY !== undefined
          ? { x: snapped.alignX, y: snapped.alignY }
          : null
    });
  });

  const reset = () => {
    frame.cancel();
    start = null;
    context?.overlay.reset();
  };

  return {
    kind: "wall",
    cursor: "crosshair",
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      const { store } = toolContext;
      const raw = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      if (!start) {
        const snapped = snapWallPoint(toolContext, raw, event.altKey);
        const point = { x: snapped.x, y: snapped.y };
        if (snapped.segment) {
          const hit = { ...snapped.segment, ...point };
          store.getState().commit((document) => splitAt(document, hit));
        }
        start = point;
        return;
      }
      const candidate = event.shiftKey ? constrainAngle(start, raw) : raw;
      const snapped = snapWallPoint(toolContext, candidate, event.altKey);
      const end = { x: snapped.x, y: snapped.y };
      const from = start;
      const wall = createWall(from, end);
      const segment = snapped.segment;
      store
        .getState()
        .commit((document) =>
          addShapes(
            segment ? splitAt(document, { ...segment, ...end }) : document,
            [wall]
          )
        );
      if (event.shiftKey) {
        start = end;
        toolContext.overlay.set({ draft: null, guides: null });
      } else {
        reset();
      }
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!start) return;
      frame.schedule({
        client: { x: event.clientX, y: event.clientY },
        shiftKey: event.shiftKey,
        altKey: event.altKey
      });
    },
    onPointerUp() {},
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && start) {
        context = toolContext;
        reset();
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};

/** Two clicks make a closed rectangular wall path. */
export const createWallRectTool = (): Tool => {
  let start: Point | null = null;
  let context: ToolContext | null = null;

  const frame = createFrameScheduler<Point>((client) => {
    if (!context || !start) return;
    const point = context.snap.snapPoint(context.toWorld(client));
    context.overlay.set({
      draft: {
        kind: "rect",
        rect: {
          x: Math.min(start.x, point.x),
          y: Math.min(start.y, point.y),
          width: Math.abs(point.x - start.x),
          height: Math.abs(point.y - start.y)
        }
      }
    });
  });

  const reset = () => {
    frame.cancel();
    start = null;
    context?.overlay.reset();
  };

  return {
    kind: "wall-rect",
    cursor: "crosshair",
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      const point = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      if (!start) {
        start = point;
        return;
      }
      const left = Math.min(start.x, point.x);
      const right = Math.max(start.x, point.x);
      const top = Math.min(start.y, point.y);
      const bottom = Math.max(start.y, point.y);
      reset();
      if (right - left < 1 || bottom - top < 1) return;
      const wallPath = createWallPath(
        [
          { x: left, y: top },
          { x: right, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom }
        ],
        { closed: true }
      );
      toolContext.store.getState().addShapes([wallPath]);
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!start) return;
      frame.schedule({ x: event.clientX, y: event.clientY });
    },
    onPointerUp() {},
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && start) {
        context = toolContext;
        reset();
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};
